import { useCallback, useState, useEffect, useRef } from 'react';
import type { SessionConfig } from '../types';
import { getSessionId } from '../utils/session';

interface UseAnalyticsOptions {
  analyticsBaseUrl: string | null | undefined;
  apiKey: string | null | undefined;
  customFields: Record<string, unknown>;
  analyticsEnabled?: boolean;
  analyticsSampleRate?: number;
  analyticsEndpoint?: string | null;
  analyticsApiKey?: string | null;
  sessionConfig?: SessionConfig;
}

interface AnalyticsState {
  sessionId: string;
  sessionStartTime: number;
  pageViews: number;
  eventsLogged: number;
  lastActivity: number;
}

interface EventData {
  duration?: number;
  toolCalls?: number;
  citations?: number;
  error?: string;
  characterCount?: number;
  [key: string]: unknown;
}

export function useAnalytics({
  analyticsBaseUrl,
  apiKey,
  customFields,
  analyticsEnabled,
  analyticsSampleRate,
  analyticsEndpoint,
  analyticsApiKey,
  sessionConfig,
}: UseAnalyticsOptions) {
  const [analyticsState, setAnalyticsState] = useState<AnalyticsState>(() => {
    const sessionId = getSessionId(sessionConfig) || '';
    return {
      sessionId,
      sessionStartTime: Date.now(),
      pageViews: 0,
      eventsLogged: 0,
      lastActivity: Date.now(),
    };
  });

  const sampleRef = useRef<boolean>(true);
  useEffect(() => {
    if (typeof analyticsSampleRate !== 'number') {
      sampleRef.current = true;
      return;
    }
    const rate = Math.max(0, Math.min(1, analyticsSampleRate));
    sampleRef.current = Math.random() <= rate;
  }, [analyticsSampleRate]);

  const resolveEndpoint = useCallback(() => {
    if (!analyticsBaseUrl) return null;
    const trimmed = analyticsBaseUrl.replace(/\/$/, '');
    if (analyticsEndpoint) {
      if (analyticsEndpoint.startsWith('http')) return analyticsEndpoint;
      return `${trimmed}${analyticsEndpoint.startsWith('/') ? '' : '/'}${analyticsEndpoint}`;
    }
    if (trimmed.endsWith('/analytics') || trimmed.endsWith('/events')) return trimmed;
    if (trimmed.includes('/chat-analytics')) return `${trimmed}/events`;
    return `${trimmed}/analytics`;
  }, [analyticsBaseUrl, analyticsEndpoint]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setAnalyticsState(prev => ({
          ...prev,
          pageViews: prev.pageViews + 1,
          lastActivity: Date.now(),
        }));
      }
    };

    const handleWindowFocus = () => {
      setAnalyticsState(prev => ({
        ...prev,
        pageViews: prev.pageViews + 1,
        lastActivity: Date.now(),
      }));
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  const sendEvent = useCallback(
    async (action: string, data: Record<string, unknown> = {}) => {
      if (!analyticsBaseUrl) return;
      if (analyticsEnabled === false) return;
      if (sampleRef.current === false) return;

      try {
        const endpoint = resolveEndpoint();
        if (!endpoint) return;
        await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(analyticsApiKey || apiKey
              ? { Authorization: `Bearer ${analyticsApiKey || apiKey}` }
              : {}),
            ...(analyticsApiKey ? { 'x-api-key': analyticsApiKey } : {}),
          },
          body: JSON.stringify({
            action,
            data: {
              ...data,
              sessionId: analyticsState.sessionId,
              sessionDuration: Date.now() - analyticsState.sessionStartTime,
              pageViews: analyticsState.pageViews,
              eventIndex: analyticsState.eventsLogged + 1,
              url: typeof window !== 'undefined' ? window.location.href : undefined,
              referrer: typeof document !== 'undefined' ? document.referrer : undefined,
            },
            timestamp: new Date().toISOString(),
            customFields,
          }),
        });

        setAnalyticsState(prev => ({
          ...prev,
          eventsLogged: prev.eventsLogged + 1,
          lastActivity: Date.now(),
        }));
      } catch (error) {
        console.warn('Analytics error:', error);
      }
    },
    [
      analyticsBaseUrl,
      analyticsEnabled,
      analyticsApiKey,
      apiKey,
      analyticsState,
      customFields,
      resolveEndpoint,
    ]
  );

  const trackAnalytics = useCallback(
    (action: string, data: Record<string, unknown> = {}) => {
      sendEvent(action, data);
    },
    [sendEvent]
  );

  return trackAnalytics;
}

export function useEnhancedAnalytics({
  analyticsBaseUrl,
  apiKey,
  customFields,
  analyticsEnabled,
  analyticsSampleRate,
  analyticsEndpoint,
  analyticsApiKey,
  sessionConfig,
}: UseAnalyticsOptions) {
  const trackAnalytics = useAnalytics({
    analyticsBaseUrl,
    apiKey,
    customFields,
    analyticsEnabled,
    analyticsSampleRate,
    analyticsEndpoint,
    analyticsApiKey,
    sessionConfig,
  });
  const [startTime, setStartTime] = useState<number>(Date.now());

  return {
    track: trackAnalytics,

    trackSession: async (data?: Record<string, unknown>) => {
      await trackAnalytics('session_start', {
        ...data,
        timestamp: new Date().toISOString(),
      });
    },

    trackMessage: async (messageType: 'user' | 'assistant', content: string, extra?: EventData) => {
      await trackAnalytics('message', {
        messageType,
        characterCount: content.length,
        ...extra,
      });
    },

    trackToolCall: async (
      toolName: string,
      status: 'started' | 'succeeded' | 'failed',
      duration?: number
    ) => {
      await trackAnalytics('tool_call', {
        toolName,
        status,
        duration,
      });
    },

    trackCitation: async (source: string, confidence?: number) => {
      await trackAnalytics('citation', {
        source,
        confidence,
      });
    },

    trackError: async (error: string, context?: Record<string, unknown>) => {
      await trackAnalytics('error', {
        error,
        ...context,
      });
    },

    trackExport: async (format: string, messageCount: number) => {
      await trackAnalytics('export', {
        format,
        messageCount,
      });
    },

    trackFileUpload: async (fileName: string, fileSize: number, status: 'success' | 'failed') => {
      await trackAnalytics('file_upload', {
        fileName,
        fileSize,
        status,
      });
    },

    trackViewChange: async (fromView: string, toView: string) => {
      await trackAnalytics('view_change', {
        fromView,
        toView,
      });
    },

    trackConversationEnd: async (totalMessages: number, satisfaction?: number) => {
      const duration = Date.now() - startTime;
      setStartTime(Date.now());

      await trackAnalytics('conversation_end', {
        totalMessages,
        duration,
        satisfaction,
      });
    },

    resetTimer: () => {
      setStartTime(Date.now());
    },

    getDuration: () => {
      return Date.now() - startTime;
    },
  };
}
